import React from 'react';
import { Image, ImageSourcePropType, StyleSheet, Text, View, ViewStyle } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';
import { EVENT_FONT, eventColors, eventGlow, GREEN_BUTTON_FILL } from './eventTheme';

type Props = {
	label: string;
	onPress?: () => void;
	disabled?: boolean;
	/** `grey`: the arcade's coming-soon PLAY - the same shape in a muted fill, without the glow. */
	tone?: 'green' | 'grey';
	width: number;
	height: number;
	fontSize: number;
	radius: number;
	borderWidth: number;
	style?: ViewStyle;
	/** Drawn before the label at the label's size, e.g. the chip in front of a price. */
	icon?: ImageSourcePropType;
};

const DISABLED_FILL: Array<[string, string]> = [
	['0', '#596450'],
	['0.5', '#465040'],
	['1', '#3F493A'],
];
/** Figma "Arcade / Play button" on the coming-soon cards (7019:79936). */
const GREY_FILL: Array<[string, string]> = [
	['0', '#4A4F45'],
	['0.6', '#30362E'],
	['1', '#21241F'],
];

/** The brief's green action - the small per-track CLAIM pill and the full-width CLAIM ALL bar. */
export const GreenButton = ({
	label,
	onPress,
	disabled = false,
	tone = 'green',
	width,
	height,
	fontSize,
	radius,
	borderWidth,
	style,
	icon,
}: Props) => {
	const fillId = useSvgId('eventGreenButton');
	const grey = tone === 'grey';
	const fill = grey ? GREY_FILL : disabled ? DISABLED_FILL : GREEN_BUTTON_FILL;

	return (
		<Pressable
			accessibilityRole='button'
			accessibilityState={{ disabled }}
			disabled={disabled}
			onPress={onPress}
			style={[
				styles.button,
				!disabled && !grey && eventGlow.green,
				{
					width,
					height,
					borderRadius: radius,
					borderWidth,
					borderColor: grey
						? '#61665C'
						: disabled
							? 'rgba(184,255,140,0.25)'
							: eventColors.greenStroke,
				},
				style,
			]}
		>
			<View pointerEvents='none' style={[styles.fill, { borderRadius: radius - borderWidth }]}>
				<Svg width='100%' height='100%'>
					<Defs>
						<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
							{fill.map(([offset, color]) => (
								<Stop key={offset} offset={offset} stopColor={color} />
							))}
						</LinearGradient>
					</Defs>
					<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${fillId})`} />
				</Svg>
			</View>
			<View style={[styles.content, { columnGap: fontSize * 0.3 }]}>
				{icon ? (
					<Image
						source={icon}
						resizeMode='contain'
						style={{ width: fontSize * 1.15, height: fontSize * 1.15 }}
					/>
				) : null}
				<Text numberOfLines={1} style={[styles.label, { fontSize }]}>
					{label}
				</Text>
			</View>
		</Pressable>
	);
};

const styles = StyleSheet.create({
	button: {
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
	},
	fill: {
		...StyleSheet.absoluteFillObject,
		overflow: 'hidden',
	},
	content: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	// Figma "Claim Button" (3161:13282): Sofia Sans Bold, gold/50.
	label: {
		color: eventColors.goldPale,
		fontFamily: EVENT_FONT,
		fontWeight: '700',
	},
});

export default GreenButton;
